import { jwtDecode } from 'jwt-decode';
import { ACCESS_TOKEN } from '@utils/constants/app-constants';
import { Roles } from '@utils/types/shared/base.ts';
import { CookieStorageExtension } from '@utils/extensions/storage.extension.ts';

export type TokenObj = {
  aud: string[];
  exp: number;
  iat: number;
  iss: string;
  nbf: number;
  uid: number;
  oid: number;
  scopes: Roles[];
};

export type TokenResult = {
  string: string | null;
  decoded: TokenObj | null;
};

export function dToken(token?: string | null): TokenObj | null {
  if (!token) return null;

  try {
    return jwtDecode<TokenObj>(token);
  } catch (error) {
    if (error) {
      TokenExtension.remove();
    }
    return null;
  }
}

export class TokenExtension {
  private static cookieOptions(): {
    secure: boolean;
    sameSite: 'Lax' | 'None';
  } {
    const secure =
      typeof window !== 'undefined' && window.location.protocol === 'https:';

    return {
      secure,
      // Embedded deployments are cross-site. HTTPS cookies must explicitly
      // opt into that context; keep Lax for local HTTP development because
      // browsers reject SameSite=None cookies without Secure.
      sameSite: secure ? 'None' : 'Lax'
    };
  }

  // Set tokens by value
  static set(accessToken: string) {
    if (!accessToken) {
      throw new Error('No token value provided.');
    }

    // Derive the cookie lifetime from the JWT's own exp claim so that:
    // - non-remembered sessions (~4h JWT) expire the cookie in ~4h
    // - remembered sessions (30d JWT) keep the cookie for the full 30d
    // Hard-coding a fixed client lifetime would fight the backend contract.
    const decoded = dToken(accessToken);
    const expiresInDays = decoded?.exp
      ? (decoded.exp - Date.now() / 1000) / 86400
      : 1;

    CookieStorageExtension.set(ACCESS_TOKEN, accessToken, {
      days: expiresInDays,
      ...this.cookieOptions()
    });
  }

  // Get tokens by key
  static get(): TokenResult {
    const decodedToken = {
      string: CookieStorageExtension.get(ACCESS_TOKEN),
      decoded: dToken(CookieStorageExtension.get(ACCESS_TOKEN))
    };
    const tokenObj = dToken(decodedToken.string);
    if (!decodedToken.string || !tokenObj || tokenObj.exp < Date.now() / 1000) {
      return {
        string: null,
        decoded: null
      };
    }

    return decodedToken;
  }

  // Erase single token by key or all tokens by all param
  static remove() {
    CookieStorageExtension.remove(ACCESS_TOKEN, this.cookieOptions());
  }
}
